import { Injectable } from '@angular/core';
import { combineQueries, Query } from '@datorama/akita';
import { IdName } from '@shared/models/id-name';
import { Procedure } from '@shared/models/procedure';
import { Observable } from 'rxjs';
import { delay, map, shareReplay } from 'rxjs/operators';
import { CaseTypeEnum } from '../models/case-type.enum';
import { CaseTypeOrderForm } from '../models/order-form';
import { OrderFormModel } from '../models/order-form-model';
import { OrderState, OrderStore } from './order-store';
import { ProcedureMap } from '@shared/models/procedure-map';
import { ShipToForNotLabsEnum } from '../models/send-to-type.enum';
import { ShellQuery } from '@shared/store/shell/shell-query';
import { TranslateService } from '@ngx-translate/core';
import { ProcedureEnum } from '@core/procedure-helpers/models/procedure.enum';
import { SendToOption } from '@shared/models/send-to-option';
import { getShipToFromOption } from '@shared/utils/send-to-utils';
import type { Maybe } from '@/types/globals';
import { isDentureRemovableFullDentureImplantBased, isImplantPlanning } from '@shared/utils/procedure-type-util';

@Injectable({ providedIn: 'root' })
export class OrderQuery extends Query<OrderState> {
	// I use delay(0) because of this problem: https://github.com/datorama/akita/issues/372
	// Don't delete it, or u'll brake the form...
	caseType$: Observable<IdName | null> = this.select(state => state.caseType).pipe(delay(0));
	treatmentStage$: Observable<Maybe<IdName>> = this.select(state => state.treatmentStage).pipe(delay(0));
	treatmentStages$: Observable<IdName[]> = this.select(state => state.treatmentStages).pipe(
		map(stages =>
			stages.map(stage => ({
				...stage,
				Name: this.translateService.instant(`TreatmentStages.${stage.Name}`)
			}))
		)
	);
	availableProcedureMaps$: Observable<Maybe<ProcedureMap[]>> = this.select(state => state.availableProcedureMaps);
	isMultiBiteSelected$: Observable<boolean> = this.select(state => state.isMultiBiteSelected).pipe(delay(0));
	isDraftSelected$: Observable<boolean> = this.select(state => state.isDraftSelected).pipe(delay(0));
	currentAlignerId$: Observable<string | null> = this.select(state => state.currentAlignerId).pipe(delay(0));
	sendTo$: Observable<SendToOption | null> = this.select(state => state.sendTo).pipe(delay(0));
	availableLabs$: Observable<SendToOption[] | null> = this.select(state => state.availableLabs);
	dueDate$: Observable<Date | null> = this.select(state => state.dueDate).pipe(delay(0));
	minimalAvailableDueDate$: Observable<Date> = this.select(state => state.minimalAvailableDueDate);
	maxDueDate$: Observable<Date | null> = this.select(state => state.maxDueDate);
	availableCaseTypeIds$: Observable<number[] | null> = this.select(state => state.availableCaseTypeIds);
	procedureMap$: Observable<ProcedureMap | null> = this.select(state => state.procedureMap);
	isFinalRecord$: Observable<boolean> = this.select(state => state.isFinalRecord);

	/** True when a procedure TypeId is selected (non-null, non-zero). */
	get hasProcedureTypeId(): boolean {
		const pm = this.procedureMap;

		return pm?.TypeId != null && pm.TypeId > 0;
	}
	procedure$: Observable<Procedure | undefined> = combineQueries([this.shellQuery.procedures$, this.procedureMap$]).pipe(
		map(([procedures, procedureMap]) => procedures.find(p => p.Id === procedureMap?.ProcedureId)),
		shareReplay({ bufferSize: 1, refCount: true })
	);
	type$: Observable<Maybe<IdName>> = combineQueries([this.shellQuery.types$, this.procedureMap$]).pipe(
		map(([types, procedureMap]) => types.find(x => x.Id === procedureMap?.TypeId)),
		shareReplay({ bufferSize: 1, refCount: true })
	);

	dentureUpperSectionIsReadonly$ = this.select(state => state.procedureMap?.DentureUpperSectionIsReadOnly);
	dentureLowerSectionIsReadonly$ = this.select(state => state.procedureMap?.DentureLowerSectionIsReadOnly);

	applianceUpperIsReadonly$ = this.select(state => state.procedureMap?.ApplianceUpperIsReadOnly);
	applianceLowerIsReadonly$ = this.select(state => state.procedureMap?.ApplianceLowerIsReadOnly);

	caseTypeOrderForm$: Observable<CaseTypeOrderForm> = combineQueries([
		this.caseType$,
		this.sendTo$,
		this.isMultiBiteSelected$,
		this.treatmentStage$,
		this.dueDate$,
		this.currentAlignerId$
	]).pipe(
		delay(0),
		map(
			([caseType, sendTo, isMultiBiteSelected, treatmentStage, dueDate, currentAlignerId]) =>
				({ caseType, sendTo, isMultiBiteSelected, treatmentStage, dueDate, currentAlignerId }) as CaseTypeOrderForm
		)
	);

	get treatmentStage(): IdName | null {
		return this.getValue().treatmentStage;
	}
	get isMultiBiteSelected(): boolean {
		return this.getValue().isMultiBiteSelected;
	}
	get isDraftSelected(): boolean {
		return this.getValue().isDraftSelected;
	}
	get directToLab(): boolean {
		return this.getValue().directToLab;
	}
	get currentAlignerId(): string | null {
		return this.getValue().currentAlignerId;
	}
	get sendTo(): SendToOption | null {
		return this.getValue().sendTo;
	}
	get dueDate(): Date | null {
		return this.getValue().dueDate;
	}
	get caseType(): IdName | null {
		return this.getValue().caseType;
	}
	get treatmentStages(): IdName[] {
		return this.getValue().treatmentStages;
	}
	get procedureMap(): ProcedureMap | null {
		return this.getValue().procedureMap;
	}
	get procedure(): Procedure | undefined {
		return this.getProcedure(this.procedureMap);
	}

	get type(): IdName | undefined {
		return this.shellQuery.rxConfiguration?.Types?.find(x => x.Id === this.procedureMap?.TypeId);
	}
	get isTreatmentStageVisible(): boolean {
		return this.getValue().isTreatmentStageVisible;
	}
	get availableCaseTypeIds(): number[] {
		return this.getValue().availableCaseTypeIds ?? [];
	}
	get availableProcedureMaps(): ProcedureMap[] {
		return this.getValue().availableProcedureMaps ?? [];
	}

	get orderModelForSave(): OrderFormModel {
		const state = this.getValue();

		return {
			MultiBiteScan: state.isMultiBiteSelected ?? false,
			TreatmentStage: state.treatmentStage?.Id,
			AlignerNumber: state.currentAlignerId ?? '',
			CaseTypeId: this.resolveEffectiveCaseTypeId(state),
			DueDate: state.dueDate as any,
			ShipToId: getShipToFromOption(state.sendTo) ?? ShipToForNotLabsEnum.midcOnly,
			ShipToName: state.sendTo?.Name as any,
			DirectToLab: state.directToLab,
			ProcedureId: state.procedureMap?.ProcedureId,
			ProcedureMapId: state.procedureMap?.Id,
			ProcedureTypeId: state.procedureMap?.TypeId
		};
	}

	// TODO: verify on modeling, exocad and sprint ray
	private resolveEffectiveCaseTypeId(state: OrderState): number {
		const derivedCaseTypeId = state.caseType?.Id ? state.caseType.Id : state.procedureMap?.CaseTypeId;

		if (this.shellQuery.isResendScan && derivedCaseTypeId === CaseTypeEnum.Restorative) {
			return state.originalCaseTypeId ?? (derivedCaseTypeId as number);
		}

		return derivedCaseTypeId as number;
	}
	

	get isDentureOrderV1(): boolean {
		return this.procedure?.Id === ProcedureEnum.Denture_Removable;
	}

	get isDentureRemovableFullDentureImplantBasedSelected(): boolean {
		return isDentureRemovableFullDentureImplantBased(this.procedure?.Id, this.procedureMap?.TypeId);
	}

	get isImplantPlanningSelected(): boolean {
		return isImplantPlanning(this.procedure?.Id);
	}

	constructor(
		protected store: OrderStore,
		private readonly shellQuery: ShellQuery,
		private readonly translateService: TranslateService
	) {
		super(store);
	}

	isCurrentCaseType$(currentCaseType: CaseTypeEnum): Observable<boolean> {
		return this.caseType$.pipe(map(caseType => caseType?.Id === currentCaseType));
	}

	private getAllProcedures(): Procedure[] | undefined {
		return this.shellQuery.rxConfiguration?.Procedures;
	}

	private getProcedure(procedureMap: Maybe<ProcedureMap>): Procedure | undefined {
		return this.getAllProcedures()?.find(p => p.Id === procedureMap?.ProcedureId);
	}
}
